import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { ADMIN_TOKEN_KEY } from "./AdminLogin";
import AvailabilityPanel from "../components/AvailabilityPanel";
import TeachersPanel from "../components/TeachersPanel";
import PartnershipsPanel from "../components/PartnershipsPanel";

function fmt(dt) {
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return dt;
  }
}

function statusColor(s) {
  if (s === "completed") return "bg-emerald-100 text-emerald-800";
  if (s === "in_progress") return "bg-amber-100 text-amber-800";
  if (s === "cancelled") return "bg-rose-100 text-rose-800";
  return "bg-stone-100 text-stone-700";
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = useMemo(() => sessionStorage.getItem(ADMIN_TOKEN_KEY) || "", []);
  const [lessons, setLessons] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [recLinkCache, setRecLinkCache] = useState({});

  useEffect(() => {
    if (!token) {
      navigate("/admin", { replace: true });
      return;
    }
    refresh();
    api.adminListTeachers(token).then(setTeachers).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function refresh() {
    setLoading(true);
    setError("");
    try {
      const data = await api.adminListLessons(token);
      setLessons(data);
    } catch (err) {
      if (err.message.includes("401") || err.message.includes("403") || err.message.toLowerCase().includes("token")) {
        sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        navigate("/admin", { replace: true });
      } else {
        setError(err.message || "Failed to load lessons");
      }
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    navigate("/admin", { replace: true });
  }

  async function deleteLesson(id) {
    if (!window.confirm("Delete this lesson and its room?")) return;
    try {
      await api.adminDeleteLesson(token, id);
      setLessons((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      alert(err.message);
    }
  }

  async function loadRecording(id) {
    try {
      const r = await api.adminGetRecording(token, id);
      setRecLinkCache((p) => ({ ...p, [id]: r.download_link }));
      window.open(r.download_link, "_blank", "noopener,noreferrer");
    } catch (err) {
      alert(err.message);
    }
  }

  async function deleteRecording(id) {
    if (!window.confirm("Delete this lesson's recording? This cannot be undone.")) return;
    try {
      await api.adminDeleteRecording(token, id);
      setRecLinkCache((p) => { const n = { ...p }; delete n[id]; return n; });
      setLessons((prev) => prev.map((l) =>
        l.id === id ? { ...l, recording_id: null, recording_status: "none" } : l
      ));
    } catch (err) {
      alert(err.message);
    }
  }

  async function assignTeacher(lessonId, teacherId) {
    try {
      const updated = await api.adminAssignTeacher(token, lessonId, teacherId || null);
      setLessons((prev) => prev.map((l) => (l.id === lessonId ? updated : l)));
    } catch (err) {
      alert(err.message);
    }
  }

  async function resendInvites(id) {
    try {
      const r = await api.adminResendInvites(token, id);
      const sent = r?.sent || {};
      const lines = [];
      if (sent.teacher) lines.push("Teacher invite sent.");
      if (sent.student) lines.push("Student invite sent.");
      if (lines.length === 0) lines.push("No emails were sent (none configured or sandbox limitation).");
      alert(lines.join("\n"));
    } catch (err) {
      alert(err.message);
    }
  }

  function copy(text) {
    navigator.clipboard?.writeText(text).catch(() => {});
  }

  return (
    <main className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-28 pb-16 px-6" data-testid="admin-dashboard-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-2">
              Administration
            </div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              Lessons
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="admin-create-lesson-btn"
              onClick={() => setShowCreate(true)}
              className="px-4 py-2 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
            >
              + New lesson
            </button>
            <button
              data-testid="admin-refresh-btn"
              onClick={refresh}
              className="px-4 py-2 bg-white border border-[#1C1C1C]/10 text-[#1C1C1C] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:border-[#1C1C1C]/40 transition-colors"
            >
              Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="px-4 py-2 text-[#1C1C1C]/60 text-xs tracking-[0.18em] uppercase font-medium hover:text-[#1C1C1C] transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 text-sm text-[#B83A3A]" data-testid="admin-dashboard-error">{error}</div>
        )}

        {loading ? (
          <div className="text-[#1C1C1C]/60">Loading…</div>
        ) : lessons.length === 0 ? (
          <div className="p-8 bg-white border border-[#1C1C1C]/10 rounded-2xl text-center text-[#1C1C1C]/60" data-testid="admin-no-lessons">
            No lessons yet. Create one to generate passcodes for the teacher and the student.
          </div>
        ) : (
          <div className="space-y-3">
            {lessons.map((l) => (
              <div
                key={l.id}
                data-testid={`admin-lesson-row-${l.id}`}
                className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-xl text-[#1C1C1C]">{l.title}</div>
                    <div className="text-sm text-[#1C1C1C]/60 mt-1">
                      {fmt(l.scheduled_start)} → {fmt(l.scheduled_end)}
                    </div>
                    {(l.teacher_name || l.student_name) && (
                      <div className="text-sm text-[#1C1C1C]/60 mt-1">
                        {l.teacher_name && <>Teacher: <strong className="text-[#1C1C1C]">{l.teacher_name}</strong></>}
                        {l.teacher_name && l.student_name && " · "}
                        {l.student_name && <>Student: <strong className="text-[#1C1C1C]">{l.student_name}</strong></>}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`text-[10px] tracking-[0.24em] uppercase px-2 py-1 rounded-full ${statusColor(l.status)}`}>
                      {l.status.replace("_", " ")}
                    </span>
                    <span className="text-[10px] tracking-[0.24em] uppercase text-[#1C1C1C]/50">
                      Recording: {l.recording_status}
                    </span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3 mt-4">
                  <PassRow
                    label="Teacher"
                    passcode={l.teacher_passcode}
                    email={l.teacher_email}
                    onCopy={copy}
                    testid={`pass-teacher-${l.id}`}
                  />
                  <PassRow
                    label="Student"
                    passcode={l.student_passcode}
                    email={l.student_email}
                    onCopy={copy}
                    testid={`pass-student-${l.id}`}
                  />
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3" data-testid={`admin-assign-teacher-${l.id}`}>
                  <span className="text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55">
                    Assigned teacher
                  </span>
                  <select
                    data-testid={`admin-assign-select-${l.id}`}
                    value={l.teacher_id || ""}
                    onChange={(e) => assignTeacher(l.id, e.target.value || null)}
                    className="px-3 py-1.5 bg-[#FAF9F6] border border-[#1C1C1C]/15 rounded-lg text-sm focus:outline-none focus:border-[#1C1C1C]/40"
                  >
                    <option value="">— Unassigned —</option>
                    {teachers.filter((t) => t.active || t.id === l.teacher_id).map((t) => (
                      <option key={t.id} value={t.id}>{t.name}{!t.active ? " (disabled)" : ""}</option>
                    ))}
                  </select>
                </div>

                {l.feedback && (
                  <div className="mt-4 p-3 bg-[#FAF9F6] rounded-xl" data-testid={`admin-feedback-${l.id}`}>
                    <div className="text-[10px] tracking-[0.24em] uppercase text-[#1C1C1C]/60 mb-1">
                      Student feedback · {l.feedback.rating}/5
                    </div>
                    {l.feedback.comments && (
                      <div className="text-sm text-[#1C1C1C]/80 italic">"{l.feedback.comments}"</div>
                    )}
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {l.recording_status === "ready" && (
                    <button
                      data-testid={`admin-recording-btn-${l.id}`}
                      onClick={() => loadRecording(l.id)}
                      className="px-3 py-1.5 bg-white border border-[#1C1C1C]/10 text-[#1C1C1C] rounded-lg text-xs tracking-[0.18em] uppercase hover:border-[#1C1C1C]/40 transition-colors"
                    >
                      {recLinkCache[l.id] ? "Re-open recording" : "Open recording"}
                    </button>
                  )}
                  {l.recording_status === "ready" && (
                    <button
                      data-testid={`admin-delete-recording-btn-${l.id}`}
                      onClick={() => deleteRecording(l.id)}
                      className="px-3 py-1.5 text-[#B83A3A] rounded-lg text-xs tracking-[0.18em] uppercase hover:bg-[#B83A3A]/5 transition-colors"
                    >
                      Delete recording
                    </button>
                  )}
                  {(l.teacher_email || l.student_email) && (
                    <button
                      data-testid={`admin-resend-btn-${l.id}`}
                      onClick={() => resendInvites(l.id)}
                      className="px-3 py-1.5 bg-white border border-[#1C1C1C]/10 text-[#1C1C1C] rounded-lg text-xs tracking-[0.18em] uppercase hover:border-[#1C1C1C]/40 transition-colors"
                    >
                      Resend invites
                    </button>
                  )}
                  <button
                    data-testid={`admin-delete-btn-${l.id}`}
                    onClick={() => deleteLesson(l.id)}
                    className="px-3 py-1.5 text-[#B83A3A] rounded-lg text-xs tracking-[0.18em] uppercase hover:bg-[#B83A3A]/5 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <AvailabilityPanel token={token} />
        <TeachersPanel token={token} />
        <PartnershipsPanel token={token} />
      </div>

      {showCreate && (
        <CreateLessonModal
          token={token}
          onClose={() => setShowCreate(false)}
          onCreated={(lesson) => {
            setLessons((prev) => [lesson, ...prev]);
            setShowCreate(false);
          }}
        />
      )}
    </main>
  );
}

function PassRow({ label, passcode, email, onCopy, testid }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const link = `${origin}/c/${passcode}`;
  return (
    <div className="bg-[#FAF9F6] rounded-xl px-3 py-2.5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[9px] tracking-[0.28em] uppercase text-[#1C1C1C]/50">{label}</div>
          <div className="font-mono text-sm tracking-wider text-[#1C1C1C]">{passcode}</div>
          {email && (
            <div className="text-[11px] text-[#1C1C1C]/55 truncate mt-0.5">{email}</div>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button
            data-testid={`${testid}-copy-passcode`}
            onClick={() => onCopy(passcode)}
            className="text-[10px] tracking-[0.24em] uppercase text-[#1C1C1C]/60 hover:text-[#1C1C1C] px-2 py-1 rounded border border-[#1C1C1C]/10"
          >
            Copy code
          </button>
          <button
            data-testid={`${testid}-copy-link`}
            onClick={() => onCopy(link)}
            className="text-[10px] tracking-[0.24em] uppercase text-[#1C1C1C]/60 hover:text-[#1C1C1C] px-2 py-1 rounded border border-[#1C1C1C]/10"
          >
            Copy link
          </button>
        </div>
      </div>
    </div>
  );
}

function pad(n) { return String(n).padStart(2, "0"); }
function defaultStart() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function defaultEnd() {
  const d = new Date(Date.now() + 2 * 60 * 60 * 1000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function CreateLessonModal({ token, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(defaultEnd);
  const [teacherName, setTeacherName] = useState("");
  const [teacherEmail, setTeacherEmail] = useState("");
  const [studentName, setStudentName] = useState("");
  const [studentEmail, setStudentEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [sendEmails, setSendEmails] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const lesson = await api.adminCreateLesson(token, {
        title,
        scheduled_start: new Date(start).toISOString(),
        scheduled_end: new Date(end).toISOString(),
        teacher_name: teacherName || null,
        teacher_email: teacherEmail || null,
        student_name: studentName || null,
        student_email: studentEmail || null,
        notes: notes || null,
        send_emails: sendEmails,
      });
      onCreated(lesson);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="admin-create-modal">
      <div className="w-full max-w-lg bg-white rounded-t-3xl md:rounded-3xl p-6 md:p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl text-[#1C1C1C]">New lesson</h2>
          <button onClick={onClose} className="text-[#1C1C1C]/60 hover:text-[#1C1C1C]" data-testid="admin-create-close">✕</button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Title">
            <input data-testid="admin-create-title" required value={title} onChange={(e) => setTitle(e.target.value)} className="input" placeholder="Spanish 101 — Verb conjugation" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start">
              <input data-testid="admin-create-start" required type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="input" />
            </Field>
            <Field label="End">
              <input data-testid="admin-create-end" required type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="input" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Teacher name (optional)">
              <input data-testid="admin-create-teacher" value={teacherName} onChange={(e) => setTeacherName(e.target.value)} className="input" />
            </Field>
            <Field label="Teacher email (optional)">
              <input data-testid="admin-create-teacher-email" type="email" value={teacherEmail} onChange={(e) => setTeacherEmail(e.target.value)} className="input" placeholder="teacher@example.com" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Student name (optional)">
              <input data-testid="admin-create-student" value={studentName} onChange={(e) => setStudentName(e.target.value)} className="input" />
            </Field>
            <Field label="Student email (optional)">
              <input data-testid="admin-create-student-email" type="email" value={studentEmail} onChange={(e) => setStudentEmail(e.target.value)} className="input" placeholder="student@example.com" />
            </Field>
          </div>
          <Field label="Notes (optional)">
            <textarea data-testid="admin-create-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input resize-y" />
          </Field>
          <label className="flex items-center gap-2 text-sm text-[#1C1C1C]/75 cursor-pointer">
            <input
              data-testid="admin-create-send-emails"
              type="checkbox"
              checked={sendEmails}
              onChange={(e) => setSendEmails(e.target.checked)}
            />
            Email magic-link invites to the addresses above
          </label>
          {error && <div className="text-sm text-[#B83A3A]" data-testid="admin-create-error">{error}</div>}
          <button
            data-testid="admin-create-submit"
            type="submit"
            disabled={loading}
            className="w-full px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors"
          >
            {loading ? "Creating…" : "Create lesson"}
          </button>
        </form>
        <style>{`.input{width:100%;padding:.65rem 1rem;background:#FAF9F6;border:1px solid rgba(28,28,28,0.1);border-radius:.75rem;font-size:0.95rem;outline:none}.input:focus{border-color:rgba(28,28,28,0.4)}`}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
